import type React from 'react';
import {
    ComboBox,
    EmptyState,
    Input,
    ListBox,
    Typography,
} from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NameOption } from '../../nameOptions';
import type { PickerKey } from '../../useNamesStep';

type NamePickerProps = {
    ariaLabel: string;
    placeholder: string;
    /** Shown when the typed text matches nothing. */
    emptyLabel: string;
    /** The description of an option that is another new text of the file. */
    fileTextLabel: string;
    value: string | null;
    /** The catalogue's entities first, then the file's other new texts. */
    options: NameOption[];
    isDisabled?: boolean;
    onChange: (key: PickerKey) => void;
};

/**
 * The Names step's searchable picker: Use existing's target and Nest
 * under's parent. A HeroUI `ComboBox` whose built-in contains-filter
 * searches each option's label; an option that is another new text of the
 * file says so in its description.
 *
 * One flat, dynamic list (`defaultItems` and a render function over option
 * objects that stay the same between renders, as `AccountParentPicker`).
 * Grouped into `ListBox.Section`s instead, filtering made React's
 * development-build render logging throw on React Aria's collection nodes
 * ("childNodes is not supported"), which left the popover stuck open.
 *
 * `getNodeTestIdProp` (data-testid only, no `id`) is used deliberately: an
 * explicit `id` on the RAC ComboBox root triggers a setState loop.
 */
export const NamePicker: React.FC<NamePickerProps> = ({
    ariaLabel,
    placeholder,
    emptyLabel,
    fileTextLabel,
    value,
    options,
    isDisabled,
    onChange,
}) => (
    <ComboBox<NameOption>
        aria-label={ariaLabel}
        className="w-full"
        defaultItems={options}
        isDisabled={isDisabled}
        menuTrigger="focus"
        onSelectionChange={onChange}
        selectedKey={value}
        {...getNodeTestIdProp('catalog-import-name-picker')}
    >
        <ComboBox.InputGroup>
            <Input placeholder={placeholder} />
            <ComboBox.Trigger />
        </ComboBox.InputGroup>
        <ComboBox.Popover>
            <ListBox<NameOption>
                renderEmptyState={() => <EmptyState>{emptyLabel}</EmptyState>}
            >
                {(option) => (
                    <ListBox.Item id={option.id} textValue={option.label}>
                        <Typography
                            className="min-w-0 flex-1"
                            slot="label"
                            type="body-sm"
                        >
                            {option.label}
                        </Typography>
                        {option.isFileText ? (
                            <Typography
                                className="shrink-0"
                                color="muted"
                                slot="description"
                                type="body-xs"
                            >
                                {fileTextLabel}
                            </Typography>
                        ) : null}
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                )}
            </ListBox>
        </ComboBox.Popover>
    </ComboBox>
);
